import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CustomDetailComponent, DataService, SharedModule, SortOrder } from '@vendure/admin-ui/core';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

const CUSTOMER_SALES_METRICS_QUERY = gql`
    query CustomerSalesMetrics($customerId: ID!, $options: ProductPurchaseQuantityListOptions) {
        productSalesMetricsByCustomer(customerId: $customerId, options: $options) {
            totalItems
            items {
                id
                quantity
                productVariant {
                    id
                    name
                    productId
                }
            }
        }
    }
`;

type ProductSalesMetricsByCustomerQuery = {
    productSalesMetricsByCustomer: {
        totalItems: number;
        items: Array<{
            id: string;
            quantity: number;
            productVariant: {
                id: string;
                name: string;
                productId: string;
            };
        }>;
    };
};

type ProductSalesMetricsByCustomerVars = {
    customerId: string;
    options?: {
        startDate?: string;
        endDate?: string;
        skip?: number;
        take?: number;
        sortByQuantity?: SortOrder;
    };
};

@Component({
    selector: 'vdr-customer-sales-metrics',
    templateUrl: './customer-sales-metrics.component.html',
    styleUrls: ['./customer-sales-metrics.component.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class CustomerSalesMetricsComponent implements CustomDetailComponent, OnInit {
    entity$!: Observable<{ id: string }>;
    detailForm!: UntypedFormGroup;
    readonly itemsPerPage$ = new BehaviorSubject<number>(10);
    readonly currentPage$ = new BehaviorSubject<number>(1);
    readonly startDate$ = new BehaviorSubject<string | null>(null);
    readonly endDate$ = new BehaviorSubject<string | null>(null);
    data$!: Observable<ProductSalesMetricsByCustomerQuery['productSalesMetricsByCustomer']>;
    items$!: Observable<ProductSalesMetricsByCustomerQuery['productSalesMetricsByCustomer']['items']>;
    totalItems$!: Observable<number>;

    constructor(private dataService: DataService) {}

    ngOnInit(): void {
        this.data$ = combineLatest([
            this.getCustomerId(),
            this.currentPage$,
            this.itemsPerPage$,
            this.startDate$,
            this.endDate$,
        ]).pipe(
            switchMap(([customerId, currentPage, itemsPerPage, startDate, endDate]) => {
                const skip = (currentPage - 1) * itemsPerPage;
                const startDateIso = this.toStartOfDayIso(startDate);
                const endDateIso = this.toEndOfDayIso(endDate);
                return this.dataService
                    .query<ProductSalesMetricsByCustomerQuery, ProductSalesMetricsByCustomerVars>(
                        CUSTOMER_SALES_METRICS_QUERY,
                        {
                            customerId,
                            options: {
                                startDate: startDateIso,
                                endDate: endDateIso,
                                skip,
                                take: itemsPerPage,
                                sortByQuantity: SortOrder.DESC,
                            },
                        },
                    )
                    .mapStream(data => data.productSalesMetricsByCustomer);
            }),
            shareReplay(1),
        );

        this.items$ = this.data$.pipe(map(data => data.items));
        this.totalItems$ = this.data$.pipe(map(data => data.totalItems));
    }

    setPageNumber(page: number): void {
        this.currentPage$.next(page);
    }

    setItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage$.next(itemsPerPage);
        this.currentPage$.next(1);
    }

    setStartDate(value: string): void {
        this.startDate$.next(value || null);
        this.currentPage$.next(1);
    }

    setEndDate(value: string): void {
        this.endDate$.next(value || null);
        this.currentPage$.next(1);
    }

    private toStartOfDayIso(value: string | null): string | undefined {
        if (!value) {
            return undefined;
        }
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date.toISOString();
    }

    private toEndOfDayIso(value: string | null): string | undefined {
        if (!value) {
            return undefined;
        }
        const date = new Date(value);
        date.setHours(23, 59, 59, 999);
        return date.toISOString();
    }

    private getCustomerId(): Observable<string> {
        return this.entity$.pipe(
            map(entity => entity?.id),
            filter((id): id is string => Boolean(id)),
            distinctUntilChanged(),
        );
    }
}
